import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Program } from '../types';
import {
  GitBranch,
  Plus,
  Edit3,
  Save,
  Sparkles,
  CheckCircle,
  ArrowRight,
  Zap,
} from 'lucide-react';

export default function Programs() {
  const [programs, setPrograms] = useState<Program[]>([]);
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [guidelineText, setGuidelineText] = useState('');

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const { data, error } = await supabase
        .from('programs')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data) {
        setPrograms(data);
        if (data.length > 0) {
          setSelectedProgram(data[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Inactive':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-amber-100 text-amber-800 border-amber-200';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading programs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full space-x-6">
      {/* Left Panel - Program List */}
      <div className="w-80 bg-white rounded-lg border border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-gray-900">Programs</h2>
            <button className="p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-gray-600">{programs.length} total</p>
        </div>

        <div className="flex-1 overflow-y-auto">
          {programs.map((program) => {
            const isSelected = selectedProgram?.id === program.id;
            return (
              <div
                key={program.id}
                onClick={() => setSelectedProgram(program)}
                className={`p-4 border-b border-gray-200 cursor-pointer transition-colors ${
                  isSelected
                    ? 'bg-purple-50 border-l-4 border-l-purple-500'
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-900">
                    {program.name}
                  </h3>
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(
                      program.status
                    )}`}
                  >
                    {program.status}
                  </span>
                </div>
                {program.description && (
                  <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                    {program.description}
                  </p>
                )}
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">
                    {program.program_type || 'General'}
                  </span>
                  <span className="text-gray-500">v{program.version}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Right Panel - Pathway Editor */}
      {selectedProgram ? (
        <div className="flex-1 space-y-6 overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {selectedProgram.name}
              </h1>
              <p className="text-gray-600 mt-1">
                {selectedProgram.description || 'No description'}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <span
                className={`px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(
                  selectedProgram.status
                )}`}
              >
                {selectedProgram.status}
              </span>
              <span className="px-3 py-1 text-sm font-medium bg-gray-100 text-gray-700 rounded-full border border-gray-200">
                Version {selectedProgram.version}
              </span>
            </div>
          </div>

          {/* Program Details */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="grid grid-cols-3 gap-6 text-sm">
              <div>
                <p className="text-gray-500">Program Type</p>
                <p className="font-medium text-gray-900 mt-1">
                  {selectedProgram.program_type || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Created</p>
                <p className="font-medium text-gray-900 mt-1">
                  {formatDate(selectedProgram.created_at)}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Last Updated</p>
                <p className="font-medium text-gray-900 mt-1">
                  {formatDate(selectedProgram.updated_at)}
                </p>
              </div>
            </div>
          </div>

          {/* Pathway Steps Visualization */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <GitBranch className="w-5 h-5 text-purple-600 mr-2" />
                Care Pathway Steps
              </h2>
              <button
                onClick={() => setEditMode(!editMode)}
                className={`px-4 py-2 rounded-lg transition-colors flex items-center ${
                  editMode
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                }`}
              >
                {editMode ? (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                ) : (
                  <>
                    <Edit3 className="w-4 h-4 mr-2" />
                    Edit Mode
                  </>
                )}
              </button>
            </div>

            <div className="space-y-4">
              {selectedProgram.pathway_steps &&
              selectedProgram.pathway_steps.length > 0 ? (
                selectedProgram.pathway_steps.map((step, index) => (
                  <div key={index} className="relative">
                    <div className="bg-gradient-to-r from-purple-50 to-purple-50 rounded-lg border border-purple-200 p-5">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0">
                          <div className="flex items-center justify-center w-10 h-10 bg-purple-600 text-white rounded-full font-bold">
                            {step.step}
                          </div>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            {step.name}
                          </h3>

                          {/* Trigger */}
                          <div className="mb-3">
                            <div className="flex items-center text-sm text-gray-700 mb-1">
                              <Zap className="w-4 h-4 text-amber-600 mr-2" />
                              <span className="font-medium">Trigger:</span>
                            </div>
                            <p className="text-sm text-gray-600 ml-6 bg-white rounded px-3 py-2 border border-purple-100">
                              {step.trigger}
                            </p>
                          </div>

                          {/* Actions */}
                          <div>
                            <div className="flex items-center text-sm text-gray-700 mb-2">
                              <CheckCircle className="w-4 h-4 text-purple-600 mr-2" />
                              <span className="font-medium">Actions:</span>
                            </div>
                            <div className="ml-6 space-y-2">
                              {step.actions.map((action, actionIndex) => (
                                <div
                                  key={actionIndex}
                                  className="flex items-start bg-white rounded px-3 py-2 border border-purple-100"
                                >
                                  <span className="flex items-center justify-center w-5 h-5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium mr-2 flex-shrink-0 mt-0.5">
                                    {actionIndex + 1}
                                  </span>
                                  <span className="text-sm text-gray-700">
                                    {action}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {editMode && (
                            <div className="mt-3 flex space-x-2">
                              <button className="px-3 py-1.5 bg-white text-purple-600 rounded hover:bg-purple-50 transition-colors text-sm border border-purple-200">
                                Edit Step
                              </button>
                              <button className="px-3 py-1.5 bg-white text-red-600 rounded hover:bg-red-50 transition-colors text-sm border border-red-200">
                                Delete Step
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Arrow between steps */}
                    {index < selectedProgram.pathway_steps.length - 1 && (
                      <div className="flex justify-center py-2">
                        <ArrowRight className="w-6 h-6 text-purple-400 transform rotate-90" />
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <GitBranch className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">
                    No pathway steps defined yet.
                  </p>
                </div>
              )}

              {editMode && (
                <button className="w-full px-4 py-3 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors border-2 border-dashed border-purple-300 flex items-center justify-center font-medium">
                  <Plus className="w-5 h-5 mr-2" />
                  Add Step
                </button>
              )}
            </div>
          </div>

          {/* AI Pathway Generator */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200 p-6">
            <div className="flex items-start mb-4">
              <Sparkles className="w-6 h-6 text-purple-600 mr-3 mt-1" />
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">
                  AI Pathway Generator
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                  Paste clinical guidelines or protocols, and our AI will
                  generate a structured care pathway with steps, triggers, and
                  actions.
                </p>

                <textarea
                  value={guidelineText}
                  onChange={(e) => setGuidelineText(e.target.value)}
                  placeholder="Paste guideline text here (e.g., diabetes care protocols, heart failure management guidelines)..."
                  className="w-full px-4 py-3 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none mb-3"
                  rows={6}
                />

                <button className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center font-medium">
                  <Sparkles className="w-5 h-5 mr-2" />
                  Generate Pathway from Guideline
                </button>
              </div>
            </div>
          </div>

          {/* Publish Workflow */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Workflow Management
            </h2>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">
                  Current Version: <span className="font-medium">v{selectedProgram.version}</span>
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Status:{' '}
                  <span
                    className={`font-medium ${
                      selectedProgram.status === 'Active'
                        ? 'text-green-600'
                        : 'text-gray-600'
                    }`}
                  >
                    {selectedProgram.status}
                  </span>
                </p>
              </div>
              <div className="flex space-x-3">
                <button className="px-6 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors border border-gray-300">
                  Create Draft
                </button>
                <button className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors">
                  Test Pathway
                </button>
                <button className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
                  Publish Live
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <GitBranch className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Select a program to view details</p>
          </div>
        </div>
      )}
    </div>
  );
}
